/**
 * Global SSE Event Deduplication Service
 * 
 * Prevents duplicate event processing on page refresh by tracking processed events
 * in a time-window based cache. Supports idempotent events and graceful degradation.
 */

interface SSEEvent {
  type: string;
  data?: {
    trade_id?: string | number;
    event_type?: string;
    timestamp?: string;
    user_id?: string;
    calculated_at?: string;
    symbol?: string;
    account?: string;
    strategy?: string;
    pnl?: number;
    priority?: number | string;
    mark_price?: number;
    qty?: number;
    old_pnl?: number;
    new_pnl?: number;
    pnl_change?: number;
    recent_count?: number;
    message?: string;
    reason?: string;
    // 🔥 NEW: Comprehensive trade snapshot properties
    trades?: any[];
    total_count?: number;
    db_count?: number;
    exchange_count?: number;
    cache_state?: {
      post_invalidation?: boolean;
      cache_stale?: boolean;
      safe_for_exchange_queries?: boolean;
      processing_mode?: string;
    };
  };
  trade_id?: string;
  client_id?: string;
  timestamp?: string;
  message?: string;
  user_id?: string;
  account?: string;
  strategy?: string;
  symbol?: string;
  pnl?: number;
  priority?: string;
}

interface ProcessedEvent {
  event_key: string;
  event_type: string;
  timestamp: number;
  client_id?: string;
  pnl?: number;
  handler_count: number; // Track how many handlers processed this
  first_seen: number;
  metadata?: Record<string, any>;
}

/**
 * Event classification for deduplication rules
 */
const EVENT_DEDUPLICATION_RULES = {
  // Critical events - MUST deduplicate to prevent corruption
  critical: ['metrics_calculated', 'trade_events', 'dashboard_refresh',
           'open_position_pnl_change', 'open_position_opened', 'open_position_closed',
           'open_position_modified', 'open_positions_refresh'],
  
  // Standard events - Safe to deduplicate
  standard: ['trade_data_updated', 'trade_hot_cache', 'test_trade_created', 
           'cache_invalidation', 'strategy_update_queued', 'comprehensive_trade_snapshot'],
  
  // Live data events - Deduplicate carefully (might need latest values)
  live: [],
  
  // System events - SKIP deduplication (expected to repeat/change)
  system: ['heartbeat', 'connected', 'error']
} as const;

type EventClassification = 'critical' | 'standard' | 'live' | 'system';

/**
 * Global SSE Event Deduplicator
 * 
 * Provides idempotent event processing by tracking events in a time-window based cache.
 * Events are identified by unique keys based on their type and content.
 */
export class GlobalSSEEventDeduplicator {
  private processedEvents = new Map<string, ProcessedEvent>();
  private readonly DEDUP_WINDOW = 5 * 60 * 1000; // 5 minutes for standard events
  private readonly TRADE_EVENTS_WINDOW = 5 * 60 * 1000; // 5 minutes for trade_events (reduced from 15 minutes)
  private readonly POSITION_EVENTS_WINDOW = 5 * 60 * 1000; // 5 minutes for position events (reduced from 10 minutes)
  private readonly DASHBOARD_REFRESH_WINDOW = 5 * 60 * 1000; // 5 minutes for dashboard_refresh (reduced from 10 minutes)
  private readonly MAX_TRACKED_EVENTS = 100; // Reduced from 200
  private readonly CLEANUP_INTERVAL = 30 * 1000; // 30 seconds
  private readonly STORAGE_KEY = 'sse_processed_events';
  private readonly STORAGE_VERSION = 'v3'; // Increment version to clear old data with new optimizations
  private readonly STORAGE_DEBOUNCE_MS = 500; // 500ms debounce
  private saveTimer: NodeJS.Timeout | null = null;
  private pendingSave = false;
  private cleanupTimer: NodeJS.Timeout | null = null;
  private memoryStats = {
    eventsTracked: 0,
    eventsSkipped: 0,
    memoryCleanups: 0,
    lastCleanupAt: 0
  };

  constructor() {
    console.log('🔄 [Global Deduplicator] Starting initialization...');
    this.loadFromStorage();
    this.startCleanupTimer();
    console.log(`🔄 [Global Deduplicator] Initialized with optimized windows (5min) - ${this.processedEvents.size} events loaded`);
  }

  /**
   * Get the appropriate expiration window for an event type
   */
  private getExpirationWindow(eventType: string): number {
    // Trade events need longer expiration since they're permanent database records
    if (eventType === 'trade_events') {
      return this.TRADE_EVENTS_WINDOW;
    }
    
    // Position events need same long expiration as trade events 
    if (eventType === 'open_position_pnl_change' || 
        eventType === 'open_position_opened' || 
        eventType === 'open_position_closed' ||
        eventType === 'open_position_modified' ||
        eventType === 'open_positions_refresh') {
      return this.POSITION_EVENTS_WINDOW;
    }
    
    // Dashboard refresh events need medium expiration
    if (eventType === 'dashboard_refresh') {
      return this.DASHBOARD_REFRESH_WINDOW;
    }
    
    // All other events use the standard window
    return this.DEDUP_WINDOW;
  }

  /**
   * Check if an event should be processed or skipped as duplicate
   */
  public shouldSkipEvent(event: SSEEvent): boolean {
    const eventClassification = this.getEventClassification(event.type);
    
    // Never deduplicate system events
    if (eventClassification === 'system') {
      return false;
    }

    const eventKey = this.getEventKey(event);
    
    // If we can't generate a key, allow the event (fail-safe)
    if (!eventKey) {
      console.warn(`⚠️ [Global Deduplicator] Could not generate key for event type: ${event.type}`);
      return false;
    }

    const isProcessed = this.isEventProcessed(eventKey);
    
    if (isProcessed) {
      this.memoryStats.eventsSkipped++;
      console.log(`�️ [DUPLICATE BLOCKED] Preventing duplicate: ${event.type} (${eventKey})`);
      
      // Update handler count for this duplicate
      const existingEvent = this.processedEvents.get(eventKey);
      if (existingEvent) {
        existingEvent.handler_count++;
      }
      
      return true;
    }

    // Mark as processed
    this.markEventProcessed(event, eventKey);
    this.memoryStats.eventsTracked++;
    
    // Special logging for trade_events to help debug
    if (event.type === 'trade_events') {
      const tradeId = event.data?.trade_id || event.trade_id;
      console.log(`✅ [Global Deduplicator] Allowing NEW trade_events: trade_id=${tradeId}, eventKey=${eventKey}`);
    } else {
      console.log(`✅ [Global Deduplicator] Allowing event: ${event.type} (${eventKey})`);
    }
    
    return false;
  }

  /**
   * Check if an event has already been processed
   */
  private isEventProcessed(eventKey: string): boolean {
    const processedEvent = this.processedEvents.get(eventKey);
    if (!processedEvent) {
      return false;
    }

    // Check if event is still within deduplication window
    const now = Date.now();
    const eventAge = now - processedEvent.timestamp;
    const expirationWindow = this.getExpirationWindow(processedEvent.event_type);
    
    if (eventAge > expirationWindow) {
      // Event has expired, remove it and allow reprocessing
      this.processedEvents.delete(eventKey);
      return false;
    }

    return true;
  }

  /**
   * Mark an event as processed
   */
  private markEventProcessed(event: SSEEvent, eventKey: string): void {
    const now = Date.now();
    
    const processedEvent: ProcessedEvent = {
      event_key: eventKey,
      event_type: event.type,
      timestamp: now,
      client_id: event.client_id,
      pnl: this.extractPnL(event),
      handler_count: 1,
      first_seen: now,
      metadata: this.extractEventMetadata(event)
    };

    this.processedEvents.set(eventKey, processedEvent);
    
    // Schedule debounced save to persistent storage
    this.debouncedSaveToStorage();
    
    // Perform memory management if we exceed limits
    if (this.processedEvents.size > this.MAX_TRACKED_EVENTS) {
      this.performMemoryCleanup();
    }
  }

  /**
   * Generate unique event key for deduplication
   */
  private getEventKey(event: SSEEvent): string | null {
    const eventType = event.type;
    
    switch (eventType) {
      case 'metrics_calculated':
        // Use calculated_at timestamp (server time) for consistent deduplication across page refreshes
        // Don't use client_id-based timestamp as it changes per page load
        const calculatedAt = event.data?.calculated_at;
        const userId = event.data?.user_id;
        return calculatedAt && userId ? `metrics_calculated:${calculatedAt}:${userId}` : null;
      
      case 'trade_events':
        // Use trade_id as primary key, or fallback to event_type for general notifications
        const tradeId = event.data?.trade_id || event.trade_id;
        const eventType = event.data?.event_type || 'trade_insert'; // Default to 'trade_insert' instead of 'unknown'
        
        console.log(`🔍 [Global Deduplicator] trade_events key generation:`, {
          tradeId, eventType, 
          dataTimestamp: event.data?.timestamp, 
          eventTimestamp: event.timestamp,
          hasData: !!event.data,
          dataKeys: event.data ? Object.keys(event.data) : []
        });
        
        if (tradeId) {
          const key = `trade_events:${tradeId}:${eventType}`;
          console.log(`🔍 [Global Deduplicator] Generated trade_events key (with trade_id): ${key}`);
          return key;
        } else {
          // For general trade notifications without specific trade_id, use event_type and timestamp
          // Use the data timestamp (more specific) or fallback to event timestamp
          const timestamp = event.data?.timestamp || event.timestamp || Date.now();
          const key = `trade_events:${eventType}:${timestamp}`;
          console.log(`🔍 [Global Deduplicator] Generated trade_events key (notification): ${key}`);
          return key;
        }
      
      case 'dashboard_refresh':
        // Use timestamp and reason
        return `dashboard_refresh:${event.data?.timestamp || Date.now()}:${event.data?.reason || 'unknown'}`;
      
      case 'trade_data_updated':
        // Use timestamp and recent count
        return `trade_data_updated:${event.data?.timestamp || Date.now()}:${event.data?.recent_count || 0}`;
      
      case 'trade_hot_cache':
        // Use account + strategy + symbol + timestamp
        const account = event.data?.account || event.account;
        const strategy = event.data?.strategy || event.strategy;
        const symbol = event.data?.symbol || event.symbol;
        return account && strategy && symbol ? 
          `trade_hot_cache:${account}:${strategy}:${symbol}:${event.data?.timestamp || Date.now()}` : null;
      
      case 'test_trade_created':
        // Use trade_id
        const testTradeId = event.trade_id;
        return testTradeId ? `test_trade_created:${testTradeId}` : null;
      
      case 'comprehensive_trade_snapshot':
        // Use timestamp and total count for deduplication
        return `comprehensive_trade_snapshot:${event.data?.timestamp || Date.now()}:${event.data?.total_count || 0}`;
      
      case 'open_position_pnl_change':
        // Use account + symbol + timestamp for PnL changes (each change should be unique)
        const posAccount = event.data?.account;
        const posSymbol = event.data?.symbol;
        const posTimestamp = event.data?.timestamp || event.timestamp;
        return posAccount && posSymbol && posTimestamp ? 
          `open_position_pnl_change:${posAccount}:${posSymbol}:${posTimestamp}` : null;
      
      case 'open_position_opened':
        // Use account + symbol + event type for position events (stable, no timestamp)
        const openedAccount = event.data?.account;
        const openedSymbol = event.data?.symbol;
        return openedAccount && openedSymbol ? 
          `open_position_opened:${openedAccount}:${openedSymbol}` : null;
      
      case 'open_position_closed':
        // Use account + symbol + event type for position events (stable, no timestamp)
        const closedAccount = event.data?.account;
        const closedSymbol = event.data?.symbol;
        return closedAccount && closedSymbol ? 
          `open_position_closed:${closedAccount}:${closedSymbol}` : null;
      
      case 'open_position_modified':
        // Use account + symbol + event type for position events (stable, no timestamp)
        const modifiedAccount = event.data?.account;
        const modifiedSymbol = event.data?.symbol;
        return modifiedAccount && modifiedSymbol ? 
          `open_position_modified:${modifiedAccount}:${modifiedSymbol}` : null;
      
      case 'open_positions_refresh':
        // Use reason only (no timestamp) - bulk refreshes should be deduplicated by reason
        return `open_positions_refresh:${event.data?.reason || 'unknown'}`;
      
      case 'cache_invalidation':
        // Use timestamp as cache invalidations are time-specific
        return `cache_invalidation:${event.timestamp || Date.now()}`;
      
      case 'strategy_update_queued':
        // Use strategy + account + timestamp
        const strategyName = event.strategy;
        const strategyAccount = event.account;
        return strategyName ? 
          `strategy_update_queued:${strategyName}:${strategyAccount || 'unknown'}:${event.timestamp || Date.now()}` : null;
      
      default:
        // For unknown event types, use type + timestamp
        console.warn(`⚠️ [Global Deduplicator] Unknown event type for key generation: ${event.type}`);
        return `${event.type}:${event.timestamp || Date.now()}`;
    }
  }

  /**
   * Get event classification for deduplication rules
   */
  private getEventClassification(eventType: string): EventClassification {
    if (EVENT_DEDUPLICATION_RULES.critical.includes(eventType as any)) {
      return 'critical';
    }
    if (EVENT_DEDUPLICATION_RULES.standard.includes(eventType as any)) {
      return 'standard';
    }
    // No more live events since they're all moved to critical
    return 'system';
  }

  /**
   * Extract PnL value from event for tracking
   */
  private extractPnL(event: SSEEvent): number | undefined {
    return event.data?.pnl || 
           event.data?.new_pnl || 
           event.data?.pnl_change || 
           (typeof event.pnl === 'number' ? event.pnl : undefined);
  }

  /**
   * Extract metadata from event for debugging
   */
  private extractEventMetadata(event: SSEEvent): Record<string, any> {
    return {
      account: event.data?.account || event.account,
      strategy: event.data?.strategy || event.strategy,
      symbol: event.data?.symbol || event.symbol,
      trade_id: event.data?.trade_id || event.trade_id,
      priority: event.data?.priority || event.priority,
      timestamp: event.data?.timestamp || event.timestamp
    };
  }

  /**
   * Reset deduplication state for new session
   */
  public resetForNewSession(): void {
    console.log('🔄 [Global Deduplicator] Resetting for new session');
    
    // Clear timers
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.pendingSave = false;
    
    this.processedEvents.clear();
    
    // Clear persistent storage
    localStorage.removeItem(this.STORAGE_KEY);
    
    this.memoryStats = {
      eventsTracked: 0,
      eventsSkipped: 0,
      memoryCleanups: 0,
      lastCleanupAt: Date.now()
    };
  }

  /**
   * Clear expired events and manage memory more aggressively - optimized for batch processing
   */
  private clearExpiredEvents(): void {
    const now = Date.now();
    let expiredCount = 0;
    const beforeSize = this.processedEvents.size;
    const keysToDelete: string[] = [];

    // Batch collect expired keys first (more efficient than individual deletes)
    for (const [key, event] of this.processedEvents.entries()) {
      const eventAge = now - event.timestamp;
      const expirationWindow = this.getExpirationWindow(event.event_type);
      if (eventAge > expirationWindow) {
        keysToDelete.push(key);
      }
    }

    // Batch delete expired events
    for (const key of keysToDelete) {
      this.processedEvents.delete(key);
      expiredCount++;
    }

    // Early memory limit enforcement
    if (this.processedEvents.size > this.MAX_TRACKED_EVENTS) {
      this.performMemoryCleanup();
    }

    if (expiredCount > 0) {
      console.log(`🧹 [Global Deduplicator] Cleared ${expiredCount} expired events (${beforeSize} → ${this.processedEvents.size})`);
    }
  }

  /**
   * Perform aggressive memory cleanup when limits are exceeded
   */
  private performMemoryCleanup(): void {
    const beforeSize = this.processedEvents.size;
    
    // If still over limit, remove oldest events (keep newest 60% of limit for more aggressive cleanup)
    if (this.processedEvents.size > this.MAX_TRACKED_EVENTS) {
      const events = Array.from(this.processedEvents.entries())
        .sort(([, a], [, b]) => a.first_seen - b.first_seen);
      
      const targetSize = Math.floor(this.MAX_TRACKED_EVENTS * 0.6); // Reduced from 0.8 to 0.6
      const eventsToRemove = this.processedEvents.size - targetSize;
      
      for (let i = 0; i < eventsToRemove && i < events.length; i++) {
        this.processedEvents.delete(events[i][0]);
      }
      
      this.memoryStats.memoryCleanups++;
      this.memoryStats.lastCleanupAt = Date.now();
      
      console.log(`🧹 [Global Deduplicator] Aggressive cleanup: removed ${eventsToRemove} oldest events (${beforeSize} → ${this.processedEvents.size})`);
    }
    
    // Save cleaned state to storage immediately (cleanup is infrequent)
    this.saveToStorage();
  }

  /**
   * Start automatic cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.clearExpiredEvents();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Get current memory statistics
   */
  public getMemoryStats(): typeof this.memoryStats & { currentlyTracked: number } {
    return {
      ...this.memoryStats,
      currentlyTracked: this.processedEvents.size
    };
  }

  /**
   * Get all currently tracked events (for debugging)
   */
  public getTrackedEvents(): ProcessedEvent[] {
    return Array.from(this.processedEvents.values());
  }

  /**
   * Load processed events from localStorage with optimized filtering
   */
  private loadFromStorage(): void {
    try {
      console.log('📦 [Global Deduplicator] Attempting to load from localStorage...');
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        console.log('📦 [Global Deduplicator] No stored events found - starting fresh');
        return;
      }

      console.log(`📦 [Global Deduplicator] Found stored data: ${stored.length} chars`);
      const parsed = JSON.parse(stored);
      
      if (parsed.version !== this.STORAGE_VERSION) {
        console.log('📦 [Global Deduplicator] Storage version mismatch - clearing old data');
        localStorage.removeItem(this.STORAGE_KEY);
        return;
      }

      const now = Date.now();
      let loadedCount = 0;
      let expiredCount = 0;

      // Bulk load events with minimal logging for performance
      for (const [key, event] of Object.entries(parsed.events as Record<string, ProcessedEvent>)) {
        const eventAge = now - event.first_seen;
        const expirationWindow = this.getExpirationWindow(event.event_type);
        
        if (eventAge <= expirationWindow) {
          this.processedEvents.set(key, event);
          loadedCount++;
        } else {
          expiredCount++;
        }
      }

      console.log(`📦 [Global Deduplicator] Loaded ${loadedCount} events, expired ${expiredCount} events`);
      
      if (loadedCount > 0) {
        console.log(`🛡️ [DEDUPLICATION ACTIVE] ${loadedCount} events ready to prevent duplicates`);
      }
    } catch (error) {
      console.warn('📦 [Global Deduplicator] Failed to load from storage:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Schedule a debounced save to storage
   */
  private debouncedSaveToStorage(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    
    this.pendingSave = true;
    this.saveTimer = setTimeout(() => {
      if (this.pendingSave) {
        this.saveToStorage();
        this.pendingSave = false;
      }
    }, this.STORAGE_DEBOUNCE_MS);
  }

  /**
   * Save processed events to localStorage
   */
  private saveToStorage(): void {
    try {
      const eventsObj: Record<string, ProcessedEvent> = {};
      const now = Date.now();
      let savedCount = 0;
      let expiredCount = 0;
      
      // Bulk save with minimal logging for performance
      for (const [key, event] of this.processedEvents.entries()) {
        const eventAge = now - event.first_seen;
        const expirationWindow = this.getExpirationWindow(event.event_type);
        if (eventAge <= expirationWindow) {
          eventsObj[key] = event;
          savedCount++;
        } else {
          expiredCount++;
        }
      }

      const dataToStore = {
        version: this.STORAGE_VERSION,
        events: eventsObj,
        savedAt: now
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToStore));
      // Reduced logging frequency for event deduplicator storage
    } catch (error) {
      console.warn('💾 [Global Deduplicator] Failed to save to storage:', error);
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    
    // Force final save before destroying
    if (this.pendingSave || this.processedEvents.size > 0) {
      this.saveToStorage();
    }
    
    this.processedEvents.clear();
    console.log('🗑️ [Global Deduplicator] Destroyed and cleaned up');
  }
}

// Export singleton instance
export const globalEventDeduplicator = new GlobalSSEEventDeduplicator();